/**
 * Sitegrow SEO - Rebuy heading hierarchy fix
 *
 * Rebuy widgets render recommended product names as <h5 class="rebuy-product-title">
 * right under the widget <h3> ("You might also like"), which skips a heading level (H3 -> H5).
 * A product name in a recommendation list is not a section heading, so it is rendered as a <p>.
 *
 * The tag is swapped inside the Vue render (vnode.tag), not directly in the DOM, so Rebuy's
 * Vue instance keeps full control of the element (re-renders, cart updates, add to cart).
 * Styling is class-based (.rebuy-product-title), so the visual result is unchanged.
 */
(() => {
  const TARGET_SELECTOR = 'h5.rebuy-product-title';
  const TARGET_CLASS = 'rebuy-product-title';
  const PATCHED_FLAG = '__sgHeadingFixPatched';

  const classString = (data) => {
    if (!data) return ' ';
    const dynamic = data.class;
    let classes = data.staticClass || '';
    if (typeof dynamic === 'string') classes += ' ' + dynamic;
    else if (Array.isArray(dynamic)) classes += ' ' + dynamic.join(' ');
    else if (dynamic && typeof dynamic === 'object') {
      classes += ' ' + Object.keys(dynamic).filter((key) => dynamic[key]).join(' ');
    }
    return ' ' + classes + ' ';
  };

  const retag = (vnode) => {
    if (!vnode) return;
    if (Array.isArray(vnode)) {
      vnode.forEach(retag);
      return;
    }
    if (vnode.tag === 'h5' && classString(vnode.data).indexOf(' ' + TARGET_CLASS + ' ') !== -1) {
      vnode.tag = 'p';
    }
    if (vnode.children) retag(vnode.children);
  };

  const patchInstance = (vm) => {
    if (!vm || vm[PATCHED_FLAG] || typeof vm.$options.render !== 'function') return;
    const originalRender = vm.$options.render;
    vm.$options.render = function patchedRender(...args) {
      const vnode = originalRender.apply(this, args);
      retag(vnode);
      return vnode;
    };
    vm[PATCHED_FLAG] = true;
    vm.$forceUpdate();
  };

  const fix = () => {
    document.querySelectorAll(TARGET_SELECTOR).forEach((element) => {
      let node = element;
      while (node && !node.__vue__) node = node.parentElement;
      // Patch the owning instance and its ancestors: the <h5> can come from a parent template (slot).
      let vm = node ? node.__vue__ : null;
      while (vm) {
        patchInstance(vm);
        vm = vm.$parent;
      }
    });
  };

  // Scheduling uses setTimeout, not requestAnimationFrame: rAF never fires in hidden/background
  // tabs and some rendering contexts, which left the Rebuy <h5> titles untouched.
  let scheduled = false;
  const scheduleFix = () => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      fix();
    }, 50);
  };

  // Perf: only react to DOM additions that are (or contain) Rebuy product titles, and start
  // observing once the page has loaded so the observer never competes with first paint.
  const REBUY_CONTAINER = '[class*="rebuy"], [data-rebuy-component], [id*="rebuy"]';
  const isRelevant = (mutations) => {
    for (let i = 0; i < mutations.length; i++) {
      const added = mutations[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.closest(REBUY_CONTAINER) || node.querySelector(TARGET_SELECTOR)) return true;
      }
    }
    return false;
  };

  const startObserving = () => {
    fix();
    new MutationObserver((mutations) => {
      if (isRelevant(mutations)) scheduleFix();
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  fix();
  document.addEventListener('DOMContentLoaded', fix);
  if (document.readyState === 'complete') startObserving();
  else window.addEventListener('load', startObserving, { once: true });
})();